import orderUtils from 'utils/Order';
import checkoutUtils from 'utils/Checkout';
import skuHelpers from 'utils/skuHelpers';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import { ALTERNATE_PAYMENT_TYPES } from 'constants/frictionlessCheckout';
import Venmo from 'utils/Venmo';
import localeUtils from 'utils/LanguageLocale';

function paymentMethodCondition(isNewUserFlow) {
    return !isNewUserFlow || checkoutUtils.isGuestOrder();
}

function getAvailablePaymentMethods(orderDetails, isNewUserFlow, klarnaError) {
    const isKlarnaEnabledForThisOrder = orderUtils.isKlarnaEnabledForThisOrder(orderDetails) && !klarnaError && paymentMethodCondition(isNewUserFlow);
    const isAfterpayEnabledForThisOrder = orderUtils.isAfterpayEnabledForThisOrder(orderDetails) && paymentMethodCondition(isNewUserFlow);
    const isPazeEnabledForThisOrder = orderUtils.isPazeEnabledForThisOrder(orderDetails) && paymentMethodCondition(isNewUserFlow);
    const isVenmoEnabledForThisOrder = orderUtils.isVenmoEnabledForThisOrder(orderDetails) && paymentMethodCondition(isNewUserFlow);
    const isAfterpayEnabledForThisProfile = checkoutUtils.isAfterpayEnabledForThisProfile(orderDetails);
    const isPayPalEnabled = orderUtils.isPayPalEnabled(orderDetails);
    const isPayPalPayLaterEligible = Sephora.configurationSettings.isPayPalEnabled && orderDetails?.items?.isPayPalPayLaterEligible;
    const isGooglePayEnabledForThisOrder = orderUtils.isGooglePayEnabledForThisOrder(orderDetails) && paymentMethodCondition(isNewUserFlow);

    return {
        isAfterpayEnabledForThisOrder,
        isAfterpayEnabledForThisProfile,
        isPayPalEnabled,
        isPayPalPayLaterEligible,
        isKlarnaEnabledForThisOrder,
        isPazeEnabledForThisOrder,
        isVenmoEnabledForThisOrder,
        isGooglePayEnabledForThisOrder
    };
}

function getGiftCardPaymentGroup(paymentGroups) {
    return (paymentGroups?.paymentGroupsEntries || []).filter(group => group.paymentGroupType === orderUtils.PAYMENT_GROUP_TYPE.GIFT_CARD);
}

function checkIfAlternateMethodSelected(afterpay, klarna, paze, venmo, googlePay = { isSelected: false }) {
    return afterpay.isSelected || klarna.isSelected || paze.isSelected || venmo.isSelected || googlePay.isSelected;
}

function getAlternatePaymentName(afterpay, klarna, venmo, paze, isNewUserFlow, googlePay = { isSelected: false }) {
    let alternatePaymentName = '';

    if (afterpay.isSelected) {
        alternatePaymentName = PAYMENT_TYPES.PAY_AFTERPAY;
    } else if (klarna.isSelected) {
        alternatePaymentName = PAYMENT_TYPES.PAY_KLARNA;
    } else if (venmo.isSelected || (isNewUserFlow && Venmo.isVenmoExpressFlow())) {
        alternatePaymentName = PAYMENT_TYPES.PAY_VENMO;
    } else if (paze.isSelected) {
        alternatePaymentName = PAYMENT_TYPES.PAY_PAZE;
    } else if (googlePay.isSelected) {
        alternatePaymentName = PAYMENT_TYPES.PAY_GOOGLE_PAY;
    }

    return alternatePaymentName;
}

function getInstallment(orderDetails) {
    const amount = orderDetails.priceInfo?.creditCardAmount || orderDetails.priceInfo.paypalAmount;
    const [installmentValue] = skuHelpers.formatInstallmentValue(amount);

    return installmentValue;
}

function checkGiftCardAmountEqualsOrderTotal(priceInfo) {
    return priceInfo?.giftCardAmount && priceInfo?.giftCardAmount === priceInfo?.orderTotal;
}

/**
 * Checks if subscription order (SDU or AutoReplenish)
 * @param {Object} orderDetails - Order details object
 * @returns {boolean} - True if subscription order
 */
function isSubscriptionOrder(orderDetails) {
    const hasAutoReplenish = orderUtils.hasAutoReplenishItems(orderDetails);
    const hasSDU = orderUtils.hasSDUInBasket(orderDetails);

    return hasAutoReplenish || hasSDU;
}

// The default-payment endpoints have inconsistent casing for PayPal: SET/UNSET
// require the backend enum `PAY_PAL`, but GET responses (and analytics probes)
// return the lowercase `paypal` form. Canonicalize both sides — lowercase with
// an explicit `pay_pal` → `paypal` alias — so equality checks stay correct
// wherever we compare payment identifiers
function normalizePaymentName(value) {
    const lower = String(value || '').toLowerCase();

    return lower === 'pay_pal' ? 'paypal' : lower;
}

function checkIsAltenateMethodDefaultPayment(alternatePaymentName, defaultPayment) {
    return (
        (alternatePaymentName === PAYMENT_TYPES.PAY_AFTERPAY && defaultPayment === 'afterpay') ||
        (alternatePaymentName === PAYMENT_TYPES.PAY_KLARNA && defaultPayment === 'klarna') ||
        (alternatePaymentName === PAYMENT_TYPES.PAY_VENMO && defaultPayment === 'venmo')
    );
}

// Paze + Gift Card split payment is US-only and gated behind the ConfigHub flag gcSplitPaymentPazeUS.
function isPazeSplitPaymentWithGCEnabled() {
    return localeUtils.isUS() && Sephora.configurationSettings?.gcSplitPaymentPazeUS?.isEnabled === true;
}

// Afterpay + Gift Card split payment (US + CA), each locale gated behind its own ConfigHub flag.
function isAfterpaySplitPaymentWithGCEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcSplitPaymentAfterpayUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcSplitPaymentAfterpayCA?.isEnabled === true)
    );
}

// Afterpay when purchasing a physical gift card (US + CA), each locale gated behind its own ConfigHub flag.
function isAfterpayGCPurchaseEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcPurchaseWithAfterpayUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcPurchaseWithAfterpayCA?.isEnabled === true)
    );
}

// PayPal when purchasing a physical gift card (US + CA), each locale gated behind its own ConfigHub flag.
function isPayPalGCPurchaseEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcPurchaseWithPayPalUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcPurchaseWithPayPalCA?.isEnabled === true)
    );
}

// Returns the { isPayPalEnabled, isPayPalPayLaterEligible } subset gated by the PayPal
// gift-card-purchase flag: passes the source values through when enabled, false when not.
// Extracted so callers can spread it into availablePaymentMethods without adding branches to the enclosing method.
function getPayPalGCPurchaseAvailability(availablePaymentMethods) {
    const enabled = isPayPalGCPurchaseEnabled();

    return {
        isPayPalEnabled: enabled ? availablePaymentMethods.isPayPalEnabled : false,
        isPayPalPayLaterEligible: enabled ? availablePaymentMethods.isPayPalPayLaterEligible : false
    };
}

function isKlarnaSplitPaymentWithGCEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcSplitPaymentKlarnaUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcSplitPaymentKlarnaCA?.isEnabled === true)
    );
}

// Klarna when purchasing a physical gift card (US + CA), each locale gated behind its own ConfigHub flag.
function isKlarnaGCPurchaseEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcPurchaseWithKlarnaUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcPurchaseWithKlarnaCA?.isEnabled === true)
    );
}

// Paze when purchasing a physical gift card is US-only, gated behind the ConfigHub flag gcPurchaseWithPazeUS.
function isPazeGCPurchaseEnabled() {
    return localeUtils.isUS() && Sephora.configurationSettings?.gcPurchaseWithPazeUS?.isEnabled === true;
}

// Returns the { isPazeEnabledForThisOrder } subset gated by the Paze gift-card-purchase flag:
// passes the source value through when enabled, false when not. Extracted so callers can spread it
// into availablePaymentMethods without adding branches to the enclosing method.
function getPazeGCPurchaseAvailability(availablePaymentMethods) {
    return {
        isPazeEnabledForThisOrder: isPazeGCPurchaseEnabled() ? availablePaymentMethods.isPazeEnabledForThisOrder : false
    };
}

// Google Pay + Gift Card split payment (US + CA), each locale gated behind its own ConfigHub flag.
function isGooglePaySplitPaymentWithGCEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcSplitPaymentGooglepayUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcSplitPaymentGooglepayCA?.isEnabled === true)
    );
}

// Apple Pay + Gift Card split payment (US + CA), each locale gated behind its own ConfigHub flag.
function isApplePaySplitPaymentWithGCEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcSplitPaymentApplepayUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcSplitPaymentApplepayCA?.isEnabled === true)
    );
}

// Note: ALTERNATE_PAYMENT_TYPES has no Apple Pay member, so Apple Pay cannot be keyed here.
// Apple Pay combinability is handled via display name in GIFT_CARD_COMBINABLE_BY_DISPLAY_NAME below.
const GIFT_CARD_COMBINABLE_ALTERNATES = {
    [ALTERNATE_PAYMENT_TYPES.PAYPAL]: () => true,
    [ALTERNATE_PAYMENT_TYPES.VENMO]: () => true,
    [ALTERNATE_PAYMENT_TYPES.PAZE]: isPazeSplitPaymentWithGCEnabled,
    [ALTERNATE_PAYMENT_TYPES.AFTERPAY]: isAfterpaySplitPaymentWithGCEnabled,
    [ALTERNATE_PAYMENT_TYPES.KLARNA]: isKlarnaSplitPaymentWithGCEnabled,
    [ALTERNATE_PAYMENT_TYPES.GOOGLE_PAY]: isGooglePaySplitPaymentWithGCEnabled
};

// Display names as pushed into unavailableMethods in NewUserPaymentView / PaymentMethodList:
// 'PayPal', 'Venmo', 'Klarna', 'Afterpay', 'Paze', 'Apple Pay', 'Google Pay'
const GIFT_CARD_COMBINABLE_BY_DISPLAY_NAME = {
    PayPal: () => true,
    Venmo: () => true,
    Klarna: isKlarnaSplitPaymentWithGCEnabled,
    Afterpay: isAfterpaySplitPaymentWithGCEnabled,
    Paze: isPazeSplitPaymentWithGCEnabled,
    'Apple Pay': isApplePaySplitPaymentWithGCEnabled,
    'Google Pay': isGooglePaySplitPaymentWithGCEnabled
};

// True when at least one unavailable method is NOT combinable with a gift card under the current
// gcSplitPayment* killswitches — i.e. a gift card is genuinely the blocker for that method.
function hasGiftCardBlockedMethod(displayNames = []) {
    return displayNames.some(name => {
        const canCombineWithGiftCard = GIFT_CARD_COMBINABLE_BY_DISPLAY_NAME[name];

        // Unknown names default to blocked (conservative: preserves legacy GC wording).
        return canCombineWithGiftCard ? !canCombineWithGiftCard() : true;
    });
}

// alternatePaymentName (PAYMENT_TYPES) => ALTERNATE_PAYMENT_TYPES. No PayPal (there is no PAY_PAYPAL key).
const PAYMENT_NAME_TO_ALTERNATE_TYPE = {
    [PAYMENT_TYPES.PAY_VENMO]: ALTERNATE_PAYMENT_TYPES.VENMO,
    [PAYMENT_TYPES.PAY_KLARNA]: ALTERNATE_PAYMENT_TYPES.KLARNA,
    [PAYMENT_TYPES.PAY_AFTERPAY]: ALTERNATE_PAYMENT_TYPES.AFTERPAY,
    [PAYMENT_TYPES.PAY_PAZE]: ALTERNATE_PAYMENT_TYPES.PAZE,
    [PAYMENT_TYPES.PAY_GOOGLE_PAY]: ALTERNATE_PAYMENT_TYPES.GOOGLE_PAY
};

function isGiftCardCombinableAlternateType(alternateType) {
    return Boolean(GIFT_CARD_COMBINABLE_ALTERNATES[alternateType]?.());
}

function isGiftCardCombinablePayment(alternatePaymentName = '') {
    return !alternatePaymentName || isGiftCardCombinableAlternateType(PAYMENT_NAME_TO_ALTERNATE_TYPE[alternatePaymentName]);
}

function isAnyPaymentOptionSelected(paymentGroups, pendingCreditCard = {}) {
    if (
        paymentGroups?.paymentGroupsEntries?.length === 1 &&
        paymentGroups?.paymentGroupsEntries?.[0].paymentGroupType === orderUtils.PAYMENT_GROUP_TYPE.CREDIT_CARD &&
        paymentGroups?.paymentGroupsEntries?.[0]?.paymentGroup?.message &&
        paymentGroups?.paymentGroupsEntries?.[0]?.paymentGroup?.cardNumber.length === 0 &&
        !pendingCreditCard?.cardNumber
    ) {
        return false;
    }

    return true;
}

export default {
    getAvailablePaymentMethods,
    getGiftCardPaymentGroup,
    checkIfAlternateMethodSelected,
    getAlternatePaymentName,
    getInstallment,
    checkGiftCardAmountEqualsOrderTotal,
    checkIsAltenateMethodDefaultPayment,
    isPazeSplitPaymentWithGCEnabled,
    isAfterpaySplitPaymentWithGCEnabled,
    isAfterpayGCPurchaseEnabled,
    isPayPalGCPurchaseEnabled,
    getPayPalGCPurchaseAvailability,
    isGiftCardCombinablePayment,
    isGiftCardCombinableAlternateType,
    isAnyPaymentOptionSelected,
    isSubscriptionOrder,
    isKlarnaSplitPaymentWithGCEnabled,
    isKlarnaGCPurchaseEnabled,
    isPazeGCPurchaseEnabled,
    getPazeGCPurchaseAvailability,
    normalizePaymentName,
    isGooglePaySplitPaymentWithGCEnabled,
    isApplePaySplitPaymentWithGCEnabled,
    hasGiftCardBlockedMethod
};
